<template>
  <div class="ms-combobox ms-editor ms-validate">
    <label
      v-if="label"
      :class="[{
        label: true,
      }]"
    >
      {{ label }}
      <span
        v-if="isRequired"
        class="required"
      />
    </label>
    <div
      :class="[{
        error: errorMessage,
        disabled: disabled,
        border: hasBorder,
        'flex-row combo-box-input': true
      }]"
    >
      <!-- Left icon -->
      <div
        v-if="leftIcon"
        :class="['icon24 left-icon', leftIcon]"
      />
      <!-- tags -->
      <div
        ref="tags"
        class="combo-box-input__tag"
      >
        <ms-tag
          v-for="item in selectedItemsDisplay"
          :key="item[valueField]"
          :title="item[displayField]"
          tag-name="div"
          class="ml-1 mt-1"
          type="info"
          size="large"
          :closable="!disabled"
          @close="onItemClick(item, null, null)"
        >
          <div class="ms-tag__text line-claim-1">
            {{ item[displayField] }}
          </div>
        </ms-tag>
      </div>
      <!-- input -->
      <input
        v-if="filterable"
        ref="input"
        v-model="queryTextInput"
        :placeholder="selected.size ? '' : placeholder"
        :class="{
          'ms-combobox__input input flex no-press': true,
          disabled: disabled
        }"
        :disabled="disabled"
        @focus="onFocusInput"
        @blur="onBlurInput"
        @keydown="onKeyDown"
        @keydown.tab="blur"
        @keydown.esc.stop.prevent="blur"
        @keydown.enter.stop.prevent="onEnter"
        @keydown.up.stop.prevent="focusPrevItem"
        @keydown.down.stop.prevent="focusNextItem"
        @keydown.f9.stop.prevent="showQuickAdd(false, true)"
        @input="debounceQuery"
        @paste="debounceQuery"
      >
      <!-- Number selected item -->
      <div
        v-if="isShowTotal"
        class="combo-box-input__selected-total"
      >
        <div class="selected-total__icon-more" />
        <div class="selected-total__number">
          {{ selected.size }}
        </div>
      </div>
      <!-- Thêm nhanh -->
      <div
        v-if="quickAdd"
        class="quickAdd"
        :class="{ hide: disabled }"
        @click="showQuickAdd"
        @keydown.enter="showQuickAdd"
      />
      <!-- icon dropdown -->
      <div
        :class="[{
          'ms-combo-box__icon-dropdown': true,
          disabled: disabled,
          'point-event-none': readOnly,
          expand: expanded,
        }]"
        @click="triggerClick"
      >
        <div class="icon20-v2 drop-down" />
      </div>
    </div>
    <div
      v-if="errorMessage && isShowText && !isGridEditor"
      class="error-text"
    >
      {{ errorMessage }}
    </div>
    <ms-combo-box-dropdown
      v-if="expanded"
      ref="dropdownPanel"
      :combo-type="comboType"
      :max-items-display="maxItemsDisplay"
      :columnx="columns"
      :loading="loading"
      :dropdown-width="dropdownWidth"
      :show-empty-text="!internalDataSource?.length"
      :topx="dropdownTopx"
      :leftx="dropdownLeftx"
      :max-widthx="dropdownMaxWidthx"
      :min-widthx="dropdownMinWidthx"
      :scroll-top="dropdownScrollTop"
      :is-check-multiple="true"
      @scrollend="dropdownScrollEnd"
      @addNew="showQuickAdd"
    >
      <ms-combo-box-item
        v-for="(item, index) in internalDataSource"
        :key="item[valueField]"
        :combo-type="comboType"
        :item="item"
        :is-selected="checkSelected(item)"
        :columnx="columns"
        :value-field="valueField"
        :is-check-multiple="true"
        :display-field="displayField"
        :class="{ 'ms-combobox-item--highlight': typeAheadPointer === index }"
        @mousedown="onItemClick(item, $event, index)"
      />
    </ms-combo-box-dropdown>
  </div>
</template>

<script>
import {
  watch,
  ref,
  nextTick,
  getCurrentInstance,
  onMounted,
  onBeforeUnmount,
  onBeforeMount,
  reactive,
  computed,
} from 'vue'
import debounce from 'lodash/debounce'

import MsComboBoxDropdown from './src/MsComboBoxDropdown.vue'
import MsComboBoxItem from './src/MsComboBoxItem.vue'
import MsTag from '@/components/tag'
import { useValidateControl } from '@/setup/validateControl.js'
import commonFn from '@/commons/commonFunction.js'
import MsBaseComponent from '@/components/base/MsBaseComponent.vue'
import popupUtil from '@/commons/popupUtil.js'
import { ComboType, FILTER_DEBOUNCE_TIME } from './src/constants'
import { MSConstant } from '@/commons/wildcards'

/* eslint-disable vue/require-default-prop */
export default {
  name: 'MsComboboxMulti',
  components: {
    MsComboBoxDropdown,
    MsComboBoxItem,
    MsTag,
  },
  extends: MsBaseComponent,
  emits: ['comboboxLoadData', 'update:modelValue'],
  props: {
    /** danh sách value đang được chọn */
    modelValue: Array,
    /** placeholder */
    placeholder: {
      default: null,
      type: [Number, String],
    },
    maxItemsDisplay: {
      default: 5,
      type: Number,
    },
    /** Cấu hình cột, dùng cho combo dạng table */
    columns: {
      type: Array,
      default: () => [],
    },
    /** Tên trường làm value */
    valueField: {
      type: String,
      default: null,
    },
    /** Tên trường làm text display */
    displayField: {
      type: String,
      default: null,
    },
    /** Dữ liệu của combobox */
    data: {
      default: () => [],
      type: Array,
    },
    /** Độ rộng của 1 item */
    itemSize: {
      default: 28,
      type: Number,
    },
    /** Chỉ nhận giá trị có trong danh sách */
    forceSelection: {
      default: true,
      type: Boolean,
    },
    /** Là combotree hay không */
    isTree: {
      default: false,
      type: Boolean,
    },
    // Parent Field
    idProperty: {
      default: 'id',
      type: String,
    },
    /** Độ rông của dropdown tính bằng px */
    dropdownWidth: {
      default: null,
      type: [String, Number],
    },
    /** Mode query lấy dữ liệu từ server hay client */
    queryMode: {
      type: String,
      default: 'local',
    },
    remoteFilter: {
      type: Boolean,
      default: false,
    },
    /** icon */
    leftIcon: {
      type: String,
      default: null,
    },
    /**
     * Câu hình quicksearch
     */
    quickSearch: {
      type: String,
    },
    /**
     * Dữ liệu truyền kèm quicksearch
     */
    quickSearchParam: {
      type: Object,
      default: null,
    },
    /**
     * Câu hình add
     */
    quickAdd: {
      type: String,
      default: null,
    },
    isShowText: {
      type: Boolean,
      default: true,
    },
    /**
     * Danh sách các trường dữ liệu lấy về
     */
    requestField: {
      type: String,
      default: null,
    },
    /**
     * size paging
     */
    pageSize: {
      type: Number,
      default: 10,
    },
    /**
     * Giá trị bản ghi tất cả
     */
    selectAllValue: {
      type: [Number, String],
      default: -1,
    },
    /**
     * Text hiển thị tất cả
     */
    selectAllText: {
      type: String,
      default: null,
    },
    /**
     * Có hiển thị bản ghi tất cả không
     */
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    // Xác định xem input có border hay không?
    hasBorder: {
      default: true,
      type: Boolean,
    },
    // Xác định các rule validate
    // Mặc định của combo sẽ là force select
    rules: {
      default: null,
      type: Array,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    /**
     * Số lượng tag hiển thị tối đa
     */
    maxSelectedDisplay: {
      type: Number,
      default: 3,
    },
    /**
     * có bật filter hay không
     */
    filterable: {
      type: Boolean,
      default: true,
    },
    /** Có chuyển các giá trị được chọn thành array không */
    isArrayValue: {
      type: Boolean,
      default: true,
    },
    /** chuỗi ngăn cách các giá trị của modelValue khi convert string <=> array */
    separatorModel: {
      type: String,
      default: MSConstant.Separator.Comma,
    },
  },
  setup (props, { emit }) {
    const queryTextInput = ref('')
    const selected = reactive(new Map())

    const internalDataSource = ref([])
    const input = ref({})
    const expanded = ref(false)
    const { proxy } = getCurrentInstance()
    const loading = ref(false)
    const dropdownScrollTop = ref(0)

    const dropdownTopx = ref(0)
    const dropdownLeftx = ref(0)
    const comboType = ref(ComboType.Normal)
    const dropdownPanel = ref(null)
    const tags = ref(null)
    const typeAheadPointer = ref(0)
    const model = computed({
      get () {
        let values = props.modelValue

        if (!props.isArrayValue) {
          values = values?.split(props.separatorModel)
        }
        return values
      },
      set (val) {
        let values = val

        if (!props.isArrayValue) {
          values = values?.join(props.separatorModel)
        }
        emit('update:modelValue', values)
      },
    })
    let lastRequestParam = null

    /** Items đã chọn sẽ được hiển thị */
    const selectedItemsDisplay = computed(() => {
      return [...selected.values()].slice(-1 * props.maxSelectedDisplay)
    })
    const isShowTotal = computed(() => {
      return selectedItemsDisplay.value.length < selected.size
    })

    // #region "Functions"

    /** Xử lý khi focus và ô input */
    const onFocusInput = () => {
      // TODO
    }
    /** Xử lý khi blur và ô input */
    const onBlurInput = () => {
      // TODO
    }

    // #region "shortkey"
    const onEsc = (e) => {
      const me = proxy

      // ẩn dropdown và stop event nếu đang expand
      if (me.expanded) {
        expanded.value = false
        e.preventDefault()
        e.cancel = true
      }
    }

    const focusNextItem = () => {
      if (!expanded.value) {
        typeAheadPointer.value = 0
        triggerClick()
      } else {
        if (
          lastRequestParam?.end &&
          internalDataSource.value.length === typeAheadPointer.value + 1
        ) {
          //
        } else {
          typeAheadPointer.value += 1
          adjustScroll()
        }
      }
    }

    const focusPrevItem = () => {
      if (typeAheadPointer.value === 0) return
      typeAheadPointer.value -= 1
      adjustScroll()
    }

    const onEnter = () => {
      if (!expanded.value) {
        return // Đang đóng thì không bắt sự kiện
      }
      if (
        typeAheadPointer.value >= 0 && typeAheadPointer.value < internalDataSource.value.length
      ) {
        onItemClick(internalDataSource.value[typeAheadPointer.value])
      }
    }
    // #endregion

    // #region "query"
    /**
     * @description Xử lý khi nhập giá trị vào ô query text
     */
    const debounceQuery = debounce((e) => {
      if (props.filterable && !props.disabled) {
        if (!expanded.value) {
          focusNextItem()
        }
        query(e.target.value)
      }
    }, FILTER_DEBOUNCE_TIME)

    /**
     * @description Query data
     */
    const query = (queryText) => {
      if (lastRequestParam?.queryText !== queryText) {
        lastRequestParam = null
        internalDataSource.value = []
      }
      switch (props.queryMode) {
        case 'local':
          localQuery(queryText)
          break
        case 'remote':
          remoteQuery(queryText)
          break
        default:
          break
      }
    }

    /**
     * Query data có sẵn ở local
     */
    const localQuery = (queryText) => {
      if (lastRequestParam?.end) {
        return
      }

      let filteredItems = lastRequestParam?.data

      filteredItems ??= props.data?.filter((item) => {
        return !queryText || item[props.displayField]?.includes(queryText)
      })
      const dataSize = filteredItems.length
      const pageSize = props.pageSize

      const payload = {
        skip: internalDataSource.value.length,
        take: pageSize,
        pageAppend: true,
        queryText: queryText,
        data: filteredItems,
        end: dataSize - internalDataSource.value.length <= pageSize,
      }

      const endIndex = Math.min(dataSize, payload.skip + payload.take)

      internalDataSource.value.push(...filteredItems.slice(payload.skip, endIndex))

      lastRequestParam = payload

      rebuildSelectedItems()
    }

    /**
     * Query lên server
     */
    const remoteQuery = (queryText) => {
      if (!lastRequestParam) {
        const payload = {
          skip: 0,
          take: props.pageSize,
          pageAppend: false,
          queryText: queryText,
        }
        const columns = [props.displayField, props.valueField].concat(
          props.columns.map((x) => x.dataField)
        )

        payload.columns = columns.distinctKey(columns).join(',')

        if (model.value) {
          if (model.value?.length > 0) {
            Object.assign(payload, {
              selectedItem: JSON.stringify({ [props.displayField]: selected }),
            })
          }
        }
        lastRequestParam = payload
        emit('comboboxLoadData', payload)
      }
    }

    const clearQuery = () => {
      queryTextInput.value = ''
    }
    // #endregion

    const blur = () => {
      expanded.value = false
      clearQuery()
      input.value.blur()
    }
    // #endregion

    const rebuildSelectedItems = (cacheData = null) => {
      const lastSelectedItems = new Map(selected)
      selected.clear()
      if (props.queryMode === 'local' && !cacheData) {
        cacheData = new Map()
        props.data?.forEach((x) =>
          cacheData.set(x[props.valueField], x)
        )
      }
      if (model.value?.length) {
        if (!cacheData) {
          cacheData = new Map()
          internalDataSource.value.forEach((x) =>
            cacheData.set(x[props.valueField], x)
          )
        }

        model.value.forEach((value) => {
          if (cacheData.has(value)) {
            const item = cacheData.get(value)

            // item.selected = true
            selected.set(item[props.valueField], item)
          } else if (lastSelectedItems.has(value)) {
            const item = lastSelectedItems.get(value)
            // item.selected = true
            selected.set(item[props.valueField], item)
          }
        })
      }
    }

    // #region "Old"

    /**
     * Click vào icon xổ xuống
     */
    const triggerClick = () => {
      const me = proxy
      const expand = expanded.value

      // disable thì không làm gì
      if (me.disabled) {
        return
      }

      if (!expand) {
        query(queryTextInput.value)
        changeDropdownPosition()
      }

      expanded.value = !expand
      nextTick(() => {
        adjustScroll()
      })

      // focus input
      input.value.focus()
    }

    /**
     * Thay đổi position khi mở dropdown
     */
    const changeDropdownPosition = () => {
      const me = proxy
      let comboEl = me.$el

      if (comboEl) {
        comboEl = comboEl.getElementsByClassName('combo-box-input')[0]
      }

      if (comboEl) {
        const comboElRect = comboEl.getBoundingClientRect()

        const windowWidth =
          window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth

        const windowHeight =
          window.innerHeight ||
          document.documentElement.clientHeight ||
          document.body.clientHeight

        me.dropdownLeftx = comboElRect.left
        me.dropdownTopx = comboElRect.top + comboElRect.height + 2
        me.dropdownMaxWidthx = me.dropdownMinWidthx = me.dropdownWidth
          ? me.dropdownWidth
          : comboElRect.width

        // Nếu chạm phải thì align sang trái
        if (comboElRect.left + me.dropdownMaxWidthx > windowWidth) {
          me.dropdownLeftx = comboElRect.right - me.dropdownMaxWidthx
        }

        // Nếu chạm bottom thì align top
        let maxItemsDisplay = me.maxItemsDisplay
        if (me.columns && me.columns.length > 0) {
          maxItemsDisplay += 1
        }
        if (
          comboElRect.bottom + me.itemSize * maxItemsDisplay + 2 >
          windowHeight
        ) {
          me.dropdownTopx =
            comboElRect.top -
            (me.itemSize *
              Math.min(maxItemsDisplay, me.internalDataSource.length) +
              16)
        }
      }
    }

    const onKeyDown = (e) => {
      emit(proxy.$ms.constant.CustomEvent.MsKeydown, e)
    }

    const getScroller = () => {
      if (expanded.value) {
        const dd = dropdownPanel.value
        if (dd && dd.$el && typeof dd.$el.querySelector === 'function') {
          return dd.$el.querySelector('.scroller-overlay')
        }
      }
      return null
    }

    const adjustScroll = () => {
      const row = dropdownPanel.value?.$el.querySelector('.combobox-item')
      if (row) {
        const rowHeight = row.getBoundingClientRect().height
        const scroller = getScroller()
        if (scroller) {
          // scroller.scrollToItem(typeAheadPointer, true);
          scroller.scrollTop = Number.parseInt(rowHeight - 0.5) * typeAheadPointer.value
        }
      }
    }

    /**
     * Khởi tạo data
     */
    const initData = (value) => {
      let cacheData = null

      if (props.queryMode === 'remote') {
        if (lastRequestParam && lastRequestParam.pageAppend) {
          cacheData = new Map()
          internalDataSource.value.concat(value).forEach((x) => {
            if (!cacheData.has(x[props.valueField])) {
              cacheData.set(x[props.displayField], x)
            }
          })
          internalDataSource.value = [...cacheData.values()]
        } else {
          // Nếu có data mới sẽ clear cache do data thêm qua quick add sẽ được add trực tiếp vào list
          internalDataSource.value = value
          selected.clear()
        }
        if (value.length < props.pageSize && lastRequestParam) {
          lastRequestParam.end = true
        }
        // Rebuild lại các selected item nếu có
        rebuildSelectedItems(cacheData)
      } else {
        localQuery()
      }
    }

    /**
     * Query thêm data nếu scroll xuống cuối
     */
    const dropdownScrollEnd = () => {
      if (props.queryMode === 'local') {
        query(queryTextInput.value)
      } else {
        if (lastRequestParam &&
          !lastRequestParam.end &&
          !(lastRequestParam.skip && lastRequestParam.skip === internalDataSource.value.length)
        ) {
          lastRequestParam.skip = internalDataSource.value.length
          lastRequestParam.pageAppend = true
          emit('comboboxLoadData', lastRequestParam)
        }
      }
    }
    /**
     * Tích chọn select hoặc ngừng select 1 item
     */
    const effectItem = (item) => {
      const isSelected = checkSelected(item) // trạng thái select hiện tại
      if (!isSelected) {
        selected.set(item[props.valueField], item)
      } else {
        selected.delete(item[props.valueField])
      }
    }

    const reset = () => {
      const me = proxy
      lastRequestParam = null
      me.internalDataSource = []
      me.cacheData = []
      selected.clear()
    }

    const getValue = () => {
      return selected && selected.size !== 0 ? selected : null
    }

    /**
     * Xử lý click vào item
     */
    const onItemClick = (item, event, index) => {
      effectItem(item)
      // TODO updateModel theo value field nếu có nhu cầu
      model.value = Array.from(selected.keys())
      if (index !== undefined && index !== null) {
        typeAheadPointer.value = index
      }
      nextTick(() => {
        setTimeout(() => {
          input.value.focus()
        }, 0)
      })
    }

    /**
     * Xử lý click ra ngoài
     */
    const clickOutSide = (e) => {
      const me = proxy
      if (
        !commonFn.hasParent(e.target, me.$el) &&
        dropdownPanel.value?.$el &&
        !commonFn.hasParent(e.target, dropdownPanel.value.$el)
      ) {
        expanded.value = false
        clearQuery()
      }
    }
    const {
      errorMessage,
      handlerValidateCombobox,
      getTitleError,
      clearValidate,
      isRequired,
    } = useValidateControl({
      props,
    })
    const validate = () => {
      if (!proxy.isGridEditor) {
        return handlerValidateCombobox()
      }
      return ''
    }

    /**
     * Call back gọi từ form quick add, search
     */
    const callbackAddData = (data, type) => {
      const fullData =
        props.queryMode === 'remote' ? internalDataSource.value : props.data
      const findValidData = (arr) => {
        switch (type) {
          case 'quickSearch':
            // [...selected.values()].forEach((x) => (x.selected = false))
            selected.clear()

            return arr.filter((item) => {
              // item.selected = true
              let cacheItem = item
              selected.set(item[props.valueField], item)
              var existed = fullData.find(
                (x) => x[props.valueField] === item[props.valueField]
              )

              if (existed) {
                // existed.selected = true
                cacheItem = existed
              }

              selected.set(cacheItem[props.valueField], item)
              return !existed
            })
          // Default sẽ vào quick add do quick add dùng các form danh mục đã có
          default:
            return arr.filter((item) => {
              // item.selected = true
              var existed = fullData.find(
                (x) => x[props.valueField] === item[props.valueField]
              )

              if (!selected.has(item[props.valueField])) {
                selected.set(item[props.valueField], item)
              }

              if (existed) {
                // existed.selected = true
                return false
              }
              return true
            })
        }
      }
      if (Array.isArray(data)) {
        internalDataSource.value.unshift(...findValidData(data))
      } else {
        internalDataSource.value.unshift(...findValidData([data]))
      }

      model.value = [...selected.keys()]
      // Trỏ scroll về thằng đầu tiên
      typeAheadPointer.value = 0
    }

    /**
     * Click vào icon quick add
     */
    const showQuickAdd = () => {
      const me = proxy
      // Thực hiện query luôn lần đầu để chạy data select
      if (props.queryMode === 'remote') {
        query(queryTextInput.value)
      }
      if (!me.loading) {
        if (!me.quickAdd) {
          return
        }
        expanded.value = false
        const param = { ...(me.quickSearchParam || {}) }
        param.mode = me.$ms.constant.FormState.Add
        if (queryTextInput.value) {
          param.quickAddText = queryTextInput.value
        }

        // Modified By NXCHIEN 01.06.2022 Sửa lại do base xử lý check options.submit
        param.options = {
          submit: callbackAddData,
          type: 'quickAdd',
        }

        // TODO => show popup quick add ở đây
        popupUtil.show(me.quickAdd, param)
      }
    }

    /**
     * Click vào icon quicksearch
     */
    const quickSearchClick = () => {
      const me = proxy
      if (me.quickSearch) {
        if (me.disabled) {
          return
        }
        me.expanded = false
        const param = { ...(me.quickSearchParam || {}) }
        popupUtil.show(
          me.quickSearch,
          Object.assign(param, {
            /**
             * Sự kiện callback khi chọn item từ form tìm nhanh
             * Modified By NXCHIEN 01.06.2022 Sửa lại do base xử lý check options.submit
             */
            options: {
              submit: callbackAddData,
              type: 'quickSearch',
            },
            selected: model.value,
            close: function () {
              me.$refs.input.focus()
            },
          })
        )
      }
    }

    const checkSelected = (item) => {
      return model.value?.includes(item[props.valueField])
    }

    // #endregion

    // #region  Hook
    onMounted(() => {
      document.addEventListener('click', clickOutSide)
      if (Array.isArray(props.data) && props.data.length > 0) {
        initData(props.data)
      }
    })
    onBeforeUnmount(() => {
      expanded.value = false
      if (dropdownPanel.value && dropdownPanel.value.$el) {
        dropdownPanel.value.$el.remove()
      }
      document.removeEventListener('click', clickOutSide)
    })
    watch(
      () => props.data,
      (value) => {
        if (Array.isArray(value) && value.length > 0) {
          initData(value)
        }
      },
      { immediate: true }
    )
    watch(
      () => model.value,
      () => {
        rebuildSelectedItems()
      },
      { immediate: true }
    )
    onBeforeMount(() => {
      const me = proxy
      if (me.isTree) {
        me.comboType = ComboType.Tree
      } else if (me.columns && me.columns.length !== 0) {
        me.comboType = ComboType.Table
      } else {
        me.comboType = ComboType.Normal
      }
    })
    // #endregion

    const getMetaData = () => {
      return JSON.parse(JSON.stringify([...selected.values()]))
    }

    return {
      internalDataSource,
      query,
      expanded,
      triggerClick,
      input,
      dropdownLeftx,
      dropdownTopx,
      loading,
      dropdownScrollEnd,
      comboType,
      dropdownScrollTop,
      validate,
      getTitleError,
      clearValidate,
      errorMessage,
      isRequired,
      onItemClick,
      reset,
      getValue,
      dropdownPanel,
      showQuickAdd,
      quickSearchClick,
      typeAheadPointer,
      onKeyDown,
      getMetaData,
      selected,

      //
      queryTextInput,
      tags,
      isShowTotal,
      selectedItemsDisplay,
      debounceQuery,
      onFocusInput,
      onBlurInput,
      onEnter,
      onEsc,
      focusNextItem,
      focusPrevItem,
      blur,
      checkSelected,
    }
  },
}
</script>
<style lang="scss" scoped>
@import '@/assets/scss/components/MsCombobox.scss';

.combo-box-input__tag {
  display: flex;
  max-height: calc(#{$input__height} - 2px); // 2px border
  max-width: calc(100% - 130px);
  overflow: hidden;

  .ms-tag {
    flex: 1;
    min-width: 0;

    &__text {
      word-break: break-all;
    }
  }
}

.combo-box-input.disabled {
  .ms-tag {
    background-color: $color-border;
  }
}

.combo-box-input__selected-total {
  display: flex;
  align-items: center;

  .selected-total__icon-more {
    height: 24px;
    width: 20px;
    position: relative;
    margin-right: 4px;

    &:before {
      content: '';
      position: absolute;
      background: transparent url($ms-image-sprites) no-repeat;
      height: 24px;
      width: 20px;
      top: 0;
      left: 0;
      background-position: -410px -164px;
    }
  }

  .selected-total__number {
    $size: 24px;

    background-color: $color-primary;
    color: $color-white;
    width: $size;
    min-width: $size;
    height: $size;
    line-height: $size;
    text-align: center;
    border-radius: 50%;
  }
}
</style>
